import React, { useState, useEffect } from 'react';
import { Modal, Form, Button, Alert } from 'react-bootstrap';
import type { Customer } from '../api/generated/model';

interface CustomerEditModalProps {
  show: boolean;
  onHide: () => void;
  customer?: Customer;
}

const CustomerEditModal: React.FC<CustomerEditModalProps> = ({ show, onHide, customer }) => {
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    contactName: '',
    contactEmail: '',
    contactPhone: '',
    addressLine1: '',
    addressLine2: '',
    city: '',
    state: '',
    zip: '',
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  // Initialize form data when customer changes or modal opens
  useEffect(() => {
    if (customer) {
      setFormData({
        name: customer.name || '',
        contactName: customer.primary_source?.contact?.name || '',
        contactEmail: customer.primary_source?.contact?.email || '',
        contactPhone: customer.primary_source?.contact?.phone || '',
        addressLine1: customer.primary_source?.address?.line1 || '',
        addressLine2: customer.primary_source?.address?.line2 || '',
        city: customer.primary_source?.address?.city || '',
        state: customer.primary_source?.address?.state || '',
        zip: customer.primary_source?.address?.zip || '',
      });
    }
  }, [customer]);

  // Handle input changes
  const handleChange = (field: keyof typeof formData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error for this field
    if (errors[field]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  // Validate form
  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Customer name is required';
    }

    // Email validation (if provided)
    if (formData.contactEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.contactEmail)) {
      newErrors.contactEmail = 'Invalid email address';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!validate()) {
      return;
    }

    // TODO: Implement save functionality
    // This would make a PATCH request to the customer API endpoint
    // For now, we'll just close the modal
    console.log('TODO: Save customer data:', formData);
    alert('Save functionality will be implemented in a future update');
    onHide();
  };

  // Handle modal close
  const handleClose = () => {
    setErrors({});
    onHide();
  };

  return (
    <Modal show={show} onHide={handleClose} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>
          {customer ? 'Edit Customer' : 'Create Customer'}
        </Modal.Title>
      </Modal.Header>
      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          <Alert variant="info">
            <i className="bi bi-info-circle me-2"></i>
            <strong>Note:</strong> Save functionality will be implemented in a future update.
            This is a placeholder modal for now.
          </Alert>

          {/* Customer Name */}
          <Form.Group className="mb-3">
            <Form.Label>
              Customer Name <span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              type="text"
              value={formData.name}
              onChange={(e) => handleChange('name', e.target.value)}
              isInvalid={!!errors.name}
              placeholder="Enter customer name"
            />
            <Form.Control.Feedback type="invalid">
              {errors.name}
            </Form.Control.Feedback>
          </Form.Group>

          {/* Contact Section */}
          <h6 className="mt-4 mb-3">Contact Information</h6>
          <Form.Group className="mb-3">
            <Form.Label>Contact Name</Form.Label>
            <Form.Control
              type="text"
              value={formData.contactName}
              onChange={(e) => handleChange('contactName', e.target.value)}
              placeholder="Enter contact name"
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Email</Form.Label>
            <Form.Control
              type="email"
              value={formData.contactEmail}
              onChange={(e) => handleChange('contactEmail', e.target.value)}
              isInvalid={!!errors.contactEmail}
              placeholder="Enter email address"
            />
            <Form.Control.Feedback type="invalid">
              {errors.contactEmail}
            </Form.Control.Feedback>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Phone</Form.Label>
            <Form.Control
              type="tel"
              value={formData.contactPhone}
              onChange={(e) => handleChange('contactPhone', e.target.value)}
              placeholder="Enter phone number"
            />
          </Form.Group>

          {/* Address Section */}
          <h6 className="mt-4 mb-3">Address</h6>
          <Form.Group className="mb-3">
            <Form.Label>Address Line 1</Form.Label>
            <Form.Control
              type="text"
              value={formData.addressLine1}
              onChange={(e) => handleChange('addressLine1', e.target.value)}
              placeholder="Street address"
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Address Line 2</Form.Label>
            <Form.Control
              type="text"
              value={formData.addressLine2}
              onChange={(e) => handleChange('addressLine2', e.target.value)}
              placeholder="Apartment, suite, unit, etc."
            />
          </Form.Group>

          <div className="row">
            <div className="col-md-5">
              <Form.Group className="mb-3">
                <Form.Label>City</Form.Label>
                <Form.Control
                  type="text"
                  value={formData.city}
                  onChange={(e) => handleChange('city', e.target.value)}
                  placeholder="City"
                />
              </Form.Group>
            </div>
            <div className="col-md-4">
              <Form.Group className="mb-3">
                <Form.Label>State</Form.Label>
                <Form.Control
                  type="text"
                  value={formData.state}
                  onChange={(e) => handleChange('state', e.target.value)}
                  placeholder="State"
                  maxLength={2}
                />
              </Form.Group>
            </div>
            <div className="col-md-3">
              <Form.Group className="mb-3">
                <Form.Label>ZIP Code</Form.Label>
                <Form.Control
                  type="text"
                  value={formData.zip}
                  onChange={(e) => handleChange('zip', e.target.value)}
                  placeholder="ZIP"
                />
              </Form.Group>
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleClose}>
            Cancel
          </Button>
          <Button variant="primary" type="submit">
            <i className="bi bi-save me-2"></i>
            Save Changes
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default CustomerEditModal;
